import React, { useEffect, useState } from "react";
import {
  BarChartSummary,
  BarChartComparison,
  VerticalPositiveNegativeBarChart,
  ComparativeTable,
  LineChartComparison
} from "../Charts";
import {
  filterData,
  processFilteredData,
  groupAgeByRange
} from "../../utils/filterFunctions";
import "./Pages.css";

const CapturasView = ({ filters, frozenKey, dates, handleFilterClick, topN, fetchWithSpinner, data, setData, comparisonYear }) => {

  useEffect(() => {
    fetchWithSpinner({
      startDate: dates.start,
      endDate: dates.end,
      year_to_compare: comparisonYear,
      endpoint: 'operatividad/capturas',
    }, setData);
  }, [dates.start, dates.end, comparisonYear]);

  const currentYear = dates.end ? new Date(dates.end).getFullYear() : new Date().getFullYear();

  const filteredData = filterData(data, filters);

  const dias = [...new Set(data.map(h => h.dia))];
  const jornadas = [...new Set(data.map(h => h.jornada))];

  const results = processFilteredData(filteredData, {
    includeTotals: true,
    groupByFields: ["mes", "articulo", "sexo", "comuna", "modalidad"],
    includeDayShift: true,
    dias,
    jornadas
  }, comparisonYear, currentYear);

  const { totals: { total1, total2, diferencia, variacion }, resumenData } = results;
  const mesData = results.groups.mes;
  const articuloDataRaw = results.groups.articulo.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const articuloData = articuloDataRaw.slice(0, Math.min(topN, articuloDataRaw.length));
  const sexoData = results.groups.sexo.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const comunaData = results.groups.comuna.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const modalidadDataRaw = results.groups.modalidad.sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));
  const modalidadData = modalidadDataRaw.slice(0, Math.min(topN, modalidadDataRaw.length));
  const diasJornadaData1 = results.diasJornadaData[comparisonYear];
  const diasJornadaData2 = results.diasJornadaData[currentYear];

  const ranges = [
    { label: "Blank", min: null, max: null },
    { label: "0-5", min: 0, max: 5 },
    { label: "6-11", min: 6, max: 11 },
    { label: "12-13", min: 12, max: 13 },
    { label: "14-17", min: 14, max: 17 },
    { label: "18-24", min: 18, max: 24 },
    { label: "25-28", min: 25, max: 28 },
    { label: "29-32", min: 29, max: 32 },
    { label: "33-38", min: 33, max: 38 },
    { label: "39-45", min: 39, max: 45 },
    { label: "46-52", min: 46, max: 52 },
    { label: "53-59", min: 53, max: 59 },
    { label: "60-66", min: 60, max: 66 },
    { label: "Mayor-67", min: 67, max: Infinity },
    { label: "Sin Dato", min: null, max: null },
  ];
  const edadData = groupAgeByRange(filteredData, ranges, comparisonYear, currentYear).sort((a, b) => (b[currentYear] + b[comparisonYear]) - (a[currentYear] + a[comparisonYear]));

  return (
    <div className="dashboard-grid">
      <div className="row top">
        <div className="card small">
          <BarChartSummary
            resumenData={resumenData}
            diferencia={diferencia}
            variacion={variacion}
            xKey="name"
            title="Resumen general"
            selectedKey={filters.año}
            frozenKey={frozenKey}
            onBarClick={(d) => handleFilterClick("año", d?.category, "Resumen general")}
          />
        </div>
        <div className="card small">
          <VerticalPositiveNegativeBarChart showData={sexoData} xKey="name" title="Sexo" selectedKey={filters.sexo} frozenKey={frozenKey} onBarClick={(d) => handleFilterClick("sexo", d?.category, "Sexo")} comparisonYear={comparisonYear} />
        </div>
        <div className="card wide">
          <BarChartComparison
            showData={articuloData}
            xKey="name"
            title="Capturas por articulo"
            selectedKey={filters.articulo}
            frozenKey={frozenKey}
            onBarClick={(d) => handleFilterClick("articulo", d?.category, "Capturas por articulo")}
          />
        </div>
      </div>
      <div className="row middle">
        <div className="column-right">
          <div className="row bottom">
            <div className="card medium">
              <VerticalPositiveNegativeBarChart
                showData={edadData}
                xKey="rango"
                title="Rango de edad"
                selectedKey={filters.rangoEdad}
                frozenKey={frozenKey}
                onBarClick={(d) => handleFilterClick("rangoEdad", d?.category, "Rango de edad")}
                comparisonYear={comparisonYear}
              />
            </div>
            <div className="card medium">
              <LineChartComparison
                showData={mesData}
                xKey="name"
                title="Capturas por mes"
                selectedKey={filters.mes}
                frozenKey={frozenKey}
                onBarClick={(d) => handleFilterClick("mes", d?.category, "Capturas por mes")}
              />
            </div>
            <div className="card medium">
              <VerticalPositiveNegativeBarChart
                showData={modalidadData}
                xKey="name"
                title="Modalidad"
                selectedKey={filters.modalidad}
                frozenKey={frozenKey}
                onBarClick={(d) => handleFilterClick("modalidad", d?.category, "Modalidad")}
                comparisonYear={comparisonYear}
              />
            </div>
          </div>
          <div className="row bottom">
            <div className="card medium">
              <BarChartComparison
                showData={comunaData}
                xKey="name"
                title="Capturas por comuna"
                selectedKey={filters.comuna}
                frozenKey={frozenKey}
                onBarClick={(d) => handleFilterClick("comuna", d?.category, "Capturas por comuna")}

              />
            </div>
            <div className="card medium">
              <ComparativeTable
                previousData={diasJornadaData1}
                previousYear={comparisonYear}
                currentData={diasJornadaData2}
                currentYear={currentYear}
                title="Capturas por día y jornada"
                selectedKey={filters.diaJornada}
                frozenKey={frozenKey}
                onBarClick={(d) => handleFilterClick("diaJornada", d?.category, "Capturas por día y jornada")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapturasView;
